import { Context, Controller } from '@hotwired/stimulus';
import { getAnchoredPosition } from '@primer/behaviors/dist/esm';
import { emit, emitter } from 'src/emitter';
import { setAttributes } from 'src/utils/dom-attributes';
import { createElement, createSvg, parseElement } from 'src/utils/dom-create';
import { toggleAttribute, toggleExpanded } from 'src/utils/dom-toggle';
import {
  emitInput,
  emitSetQuery,
  emitSetState,
  EVENT_CHANGED_MODAL_LAYOUT,
  EVENT_ESCAPE,
  EVENT_PICK_OPERATOR,
  EVENT_PICK_TYPE,
  EVENT_PICK_VALUE,
  isEventPickValue,
} from './search_bar/events';
import {
  SearchBarState,
  STATE_BLURRED,
  STATE_TOKEN_OPERATOR,
  STATE_TOKEN_OPERATOR_ACTIVE,
  STATE_TOKEN_TYPE,
  STATE_TOKEN_TYPE_ACTIVE,
  STATE_TOKEN_VALUE,
  STATE_TOKEN_VALUE_ACTIVE,
} from './search_bar/states';
import {
  isValidOperator,
  isValidTokenType,
  Token,
  TOKEN_OPERATOR_ANY_OF,
  TOKEN_OPERATOR_EQUALS,
  TOKEN_OPERATOR_INVALID,
  TOKEN_OPERATOR_NEQUALS,
  TOKEN_TYPE_ALIAS_ASSET_TYPE,
  TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE,
  TOKEN_TYPE_ALIAS_LOCATED_AT,
  TOKEN_TYPE_ALIAS_NAME,
  TOKEN_TYPE_ASSIGNEE,
  TOKEN_TYPE_AUTHOR,
  TOKEN_TYPE_CONFIDENTIAL,
  TOKEN_TYPE_INVALID,
  TOKEN_TYPE_LABEL,
  TOKEN_TYPE_REL,
  TOKEN_TYPE_ROLE,
  TOKEN_TYPE_SITE,
  TOKEN_TYPE_TITLE,
  TokenOperator,
  TokenType,
} from './search_bar/tokens';

export default class SearchBarController extends Controller {
  public static values = {
    supported: String,
    autocompleteUrls: Object,
    resolveUrls: Object,
    maxAutocompleteOptions: {
      type: Number,
      default: 12,
    },
  };

  public static targets = [
    'label',
    'input',
    'inputGroup',
    'inputContainer',
    'icon',
  ];

  declare private labelTarget: HTMLLabelElement;
  declare private inputTarget: HTMLInputElement;
  declare private inputContainerTarget: HTMLElement;
  declare private inputGroupTarget: HTMLElement;
  declare private iconTarget: HTMLElement;
  declare private supportedValue: string;
  declare private autocompleteUrlsValue: Partial<
    Record<'labels' | 'sites' | 'users', string>
  >;
  declare private maxAutocompleteOptionsValue: number;
  declare private resolveUrlsValue: Partial<
    Record<'label' | 'site' | 'user', string>
  >;

  private tokenTypeModal: HTMLElement | undefined;
  private tokenOperatorModal: HTMLElement | undefined;
  private tokenValueModal: HTMLElement | undefined;
  private hiddenInput: HTMLInputElement | undefined;
  private clearButton: HTMLButtonElement | undefined;

  private enabledTokenTypes: TokenType[] = [];
  private tokens: Token[] = [];
  private state: SearchBarState = STATE_BLURRED;
  private nextToken: Token;
  private debounceTimerForHiddenInput:
    ReturnType<typeof setTimeout> | undefined;

  constructor(context: Context) {
    super(context);

    this.onFocusInput = this.onFocusInput.bind(this);
    this.onBlurInput = this.onBlurInput.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onInput = this.onInput.bind(this);

    this.onPickedTokenType = this.onPickedTokenType.bind(this);
    this.onPickedTokenOperator = this.onPickedTokenOperator.bind(this);
    this.onPickedTokenValue = this.onPickedTokenValue.bind(this);
    this.onChangedModalLayout = this.onChangedModalLayout.bind(this);

    this.nextToken = new Token(TOKEN_TYPE_INVALID, TOKEN_OPERATOR_INVALID, '');
  }

  connect(): void {
    this.enabledTokenTypes = this.validateTokenTypes(
      this.supportedValue.split(' '),
    );
    this.tokens = this.tokenize(this.inputTarget.value);

    this.inflateTokenTypeMenu();
    this.inflateTokenOperatorMenu();
    this.inflateTokenValueMenu();

    this.inputTarget.setAttribute('autocomplete', 'off');
    this.inputTarget.addEventListener('focus', this.onFocusInput);
    this.inputTarget.addEventListener('blur', this.onBlurInput);
    this.inputTarget.addEventListener('keydown', this.onKeyDown);
    this.inputTarget.addEventListener('input', this.onInput);

    emitter.on(EVENT_PICK_TYPE, this.onPickedTokenType);
    emitter.on(EVENT_PICK_OPERATOR, this.onPickedTokenOperator);
    emitter.on(EVENT_PICK_VALUE, this.onPickedTokenValue);
    emitter.on(EVENT_CHANGED_MODAL_LAYOUT, this.onChangedModalLayout);

    this.inflateClearQuery();
    this.buttonizeIcon();

    this.tokens.forEach((token) => {
      if (token.value) {
        this.drawToken(token);
      }
    });

    this.inflateHiddenInput();

    this.inputGroupTarget.classList.add('overflow-x-auto');
    this.inputTarget.classList.add('min-w-[240px]');
    this.labelTarget.classList.add('sr-only');
  }

  disconnect(): void {
    if (this.debounceTimerForHiddenInput) {
      clearTimeout(this.debounceTimerForHiddenInput);
      this.debounceTimerForHiddenInput = undefined;
    }

    this.deflateTokenTypeMenu();
    this.deflateTokenOperatorMenu();
    this.deflateTokenValueMenu();

    this.inputTarget.removeAttribute('autocomplete');
    this.inputTarget.removeEventListener('focus', this.onFocusInput);
    this.inputTarget.removeEventListener('blur', this.onBlurInput);
    this.inputTarget.removeEventListener('keydown', this.onKeyDown);
    this.inputTarget.removeEventListener('input', this.onInput);

    emitter.off(EVENT_PICK_TYPE, this.onPickedTokenType);
    emitter.off(EVENT_PICK_OPERATOR, this.onPickedTokenOperator);
    emitter.off(EVENT_PICK_VALUE, this.onPickedTokenValue);
    emitter.off(EVENT_CHANGED_MODAL_LAYOUT, this.onChangedModalLayout);

    this.deflateClearQuery();
    this.deButtonizeIcon();

    this.tokens.forEach((token) => {
      this.removeToken({ params: { id: token.id } });
    });

    this.deflateHiddenInput();

    this.inputGroupTarget.classList.remove('overflow-x-auto');
    this.inputTarget.classList.remove('min-w-[240px]');
    this.labelTarget.classList.remove('sr-only');

    this.tokens.splice(0, this.tokens.length);
    this.enabledTokenTypes.splice(0, this.enabledTokenTypes.length);
  }

  private validateTokenTypes(supported: string[]): TokenType[] {
    return supported.filter(isValidTokenType);
  }

  private tokenize(query: string): Token[] {
    console.debug('[search-bar] initial:', query);

    const matches = Array.from(query.matchAll(/(?:"(?:\\.|[^"])*"|[^" ])+/g));
    const queryParts: [string, string | null, string | null][] = matches.map(
      (match) => {
        const part = match[0];
        if (part[0] === '"' && part[part.length - 1] === '"') {
          return [part, null, null];
        }

        const [key, operatorOrValue, value, ...more] = part.split(':');

        // role:tag:...
        if (value === 'tag' && more.length > 0) {
          return [key, operatorOrValue, [value, ...more].join(':')];
        }

        // role:=:tag:...
        if (operatorOrValue === 'tag' && value.length > 0) {
          return [key, null, [operatorOrValue, value, ...more].join(':')];
        }

        if (value) {
          return [key, operatorOrValue, value];
        } else {
          return [key, null, operatorOrValue];
        }
      },
    );

    // let tokenState: Token | undefined;
    // let tokenSort: Token | undefined;

    const tokens: Token[] = [];

    queryParts.forEach(([key, operator, value]) => {
      switch (key) {
        case 'status':
        case 'state': {
          // NOTE: tokenState = new Token('status', TOKEN_OPERATOR_EQUALS, value)
          return;
        }

        case 'sort':
        case 'order': {
          // NOTE: tokenSort = new Token('sort', TOKEN_OPERATOR_EQUALS, value)
          return;
        }

        default: {
          if (!value) {
            tokens.push(
              new Token(TOKEN_TYPE_TITLE, TOKEN_OPERATOR_EQUALS, key),
            );
          } else {
            let tokenOperator: TokenOperator = TOKEN_OPERATOR_ANY_OF;

            if (isValidOperator(operator)) {
              tokenOperator = operator;
            } else if (key.endsWith('!')) {
              key = key.substring(0, key.length - 1);
              tokenOperator = TOKEN_OPERATOR_EQUALS;
            } else if (key.endsWith('~')) {
              key = key.substring(0, key.length - 1);
              tokenOperator = TOKEN_OPERATOR_NEQUALS;
            }

            if (isValidTokenType(key)) {
              tokens.push(new Token(key, tokenOperator, value));
            } else {
              console.warn('[search] invalid token type', key);
            }
          }
        }
      }
    });

    return tokens;
  }

  private inflateTokenTypeMenu() {
    this.inputContainerTarget.classList.add('relative');

    setAttributes(this.inputTarget, {
      'aria-expanded': false,
      'aria-controls': [
        this.inputTarget.getAttribute('aria-controls') || '',
        'token-type-options',
      ]
        .join(' ')
        .trim(),
    });

    this.tokenTypeModal = createElement('div', {
      id: 'token-type-options',
      'data-controller': 'search-bar--token-type-modal',
      hidden: '',
    });

    this.tokenTypeModal.classList.add(
      'absolute',
      // 'transition',
      // 'ease-in',
      // 'duration-75',
      'transform',
      // 'opacity-1',
      // 'scale-95',
      'top-[36px]',
      'left-[12px]',
      'z-10',
    );

    const disclosureWrapper = createElement(
      'div',
      {
        'data-component': 'generic/disclosure-menu',
        role: 'listbox',
        class: 'p-1',
        style:
          'min-width: 200px; max-width: 380px; max-width: clamp(200px, 80vw, 240px);',
      },
      /*createElement(
        'div',
        {
          class: 'disclosure-menu__header sr-only',
          role: 'none',
        },
        [
          createElement(
            'p',
            { role: 'none', class: 'sr-only },
            createElement('strong', { class: 'sr-only' }, 'Token type options'),
          ),
        ],
      ),*/
    );

    const menuGroup = createElement('div', {
      role: 'none',
      class: 'disclosure-menu__item-group',
    });

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_ASSIGNEE)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param': TOKEN_TYPE_ASSIGNEE,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<style>.stroke { stroke: light-dark(#002e72, #002e72); }
.fill { fill: light-dark(#fff, #fff); }</style>
<path class="fill" d="M3.25 4.75a4.25 4.25 0 1 0 8.5 0 4.25 4.25 0 1 0-8.5 0M.5 17.5a7 7 0 0 1 14 0Z" />
<path fill="#adbcb9" d="M7.5 3a4.24 4.24 0 0 1 4.042 3 4.2 4.2 0 0 0 .208-1.25 4.25 4.25 0 0 0-8.5 0A4.2 4.2 0 0 0 3.458 6 4.24 4.24 0 0 1 7.5 3M7.5 13a7 7 0 0 1 6.532 4.5h.468a7 7 0 0 0-14 0h.468A7 7 0 0 1 7.5 13" />
<path fill="none" class="stroke" stroke-linecap="round" stroke-linejoin="round" d="M3.25 4.75a4.25 4.25 0 1 0 8.5 0 4.25 4.25 0 1 0-8.5 0M8.5 17.5h-8a7 7 0 0 1 10.783-5.89" />
<path fill="#00cbe0" d="M17.5 23.5a6 6 0 1 0-6-6 6 6 0 0 0 6 6" />
<path class="fill" d="M17.5 11.5a6 6 0 0 0-3.943 10.516l8.458-8.458A5.98 5.98 0 0 0 17.5 11.5" />
<path fill="none" class="stroke" stroke-linecap="round" stroke-linejoin="round" d="M17.5 23.5a6 6 0 1 0-6-6 6 6 0 0 0 6 6" />
<path fill="none" class="stroke" stroke-linecap="round" stroke-linejoin="round" d="m20.174 15.758-2.9 3.874a.75.75 0 0 1-1.13.08l-1.5-1.5" />`,
            ),
            createElement('span', { class: 'font-medium' }, 'Assignee'),
          ],
        ),
      );
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_AUTHOR)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param': TOKEN_TYPE_AUTHOR,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path fill="#fff" d="M3.25 4.75a4.25 4.25 0 1 0 8.5 0 4.25 4.25 0 1 0-8.5 0M.5 17.5a7 7 0 0 1 14 0Z" />
<path fill="#adbcb9" d="M7.5 3a4.24 4.24 0 0 1 4.042 3 4.2 4.2 0 0 0 .208-1.25 4.25 4.25 0 0 0-8.5 0A4.2 4.2 0 0 0 3.458 6 4.24 4.24 0 0 1 7.5 3M7.5 13a7 7 0 0 1 6.532 4.5h.468a7 7 0 0 0-14 0h.468A7 7 0 0 1 7.5 13" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M3.25 4.75a4.25 4.25 0 1 0 8.5 0 4.25 4.25 0 1 0-8.5 0M10.5 17.5H.5A7 7 0 0 1 13.563 14" />
<path fill="#00cbe0" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="m22.881 15.118-7.632 7.63-3.75.75.75-3.75 7.632-7.63a2.11 2.11 0 0 1 2.991 0l.009.008a2.116 2.116 0 0 1 0 2.992" />
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Author'),
          ],
        ),
      );
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_CONFIDENTIAL)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param':
              TOKEN_TYPE_CONFIDENTIAL,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path fill="#adbcb9" d="M.914 12.594a.99.99 0 0 1 0-1.188C2.114 9.8 5.9 5.5 12 5.5s9.886 4.3 11.086 5.906a.99.99 0 0 1 0 1.188C21.886 14.2 18.1 18.5 12 18.5S2.114 14.2.914 12.594" />
<path fill="#00cbe0" d="M7.5 12a4.5 4.5 0 1 0 9 0 4.5 4.5 0 1 0-9 0" />
<path fill="#fff" d="M8.818 15.182a4.5 4.5 0 0 1 6.364-6.364Z" />
<path fill="#00cbe0" d="M8.78 5.917a15.05 15.05 0 0 0-7.866 5.489.984.984 0 0 0 0 1.188C2.114 14.2 5.9 18.5 12 18.5a12.5 12.5 0 0 0 3.22-.417Z" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M.914 12.594a.99.99 0 0 1 0-1.188C2.114 9.8 5.9 5.5 12 5.5s9.886 4.3 11.086 5.906a.99.99 0 0 1 0 1.188C21.886 14.2 18.1 18.5 12 18.5S2.114 14.2.914 12.594" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M9.885 8.005A5 5 0 0 1 12 7.5a4.5 4.5 0 0 1 4.5 4.5 4.33 4.33 0 0 1-2.394 3.978M7.5 3.5l9 17M6 7l6 11.5M3.047 9.089l4.566 8.624" />
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Confidential'),
          ],
        ),
      );
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_LABEL)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param': TOKEN_TYPE_LABEL,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path fill="#00cbe0" d="M23.028 14.543 9.934 1.442A2.8 2.8 0 0 0 8.227.735H1.736a1 1 0 0 0-1 1l.006 6.494a2.8 2.8 0 0 0 .708 1.707l13.092 13.091a.551.551 0 0 0 1-.249l1.693-5.543 5.544-1.692a.552.552 0 0 0 .249-1M5.735 7.735a2 2 0 1 1 2-2 2 2 0 0 1-2 2" />
<path fill="#fff" d="M4.321 4.321A2 2 0 0 1 7.15 7.15l10.085 10.085 5.544-1.692a.552.552 0 0 0 .249-1L9.934 1.442A2.8 2.8 0 0 0 8.227.735H1.736a1 1 0 0 0-.707.294Z" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M23.028 14.543 9.934 1.442A2.8 2.8 0 0 0 8.227.735H1.736a1 1 0 0 0-1 1l.006 6.494a2.8 2.8 0 0 0 .708 1.707l13.092 13.091a.551.551 0 0 0 1-.249l1.693-5.543 5.544-1.692a.552.552 0 0 0 .249-1M5.735 7.735a2 2 0 1 1 2-2 2 2 0 0 1-2 2" />
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Labelled'),
          ],
        ),
      );
    }

    // occurred
    /*if (this.enabledTokenTypes.includes(TOKEN_TYPE_AFTER)) {
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_BEFORE)) {
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_RANGE)) {
    }*/

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_REL)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param': TOKEN_TYPE_REL,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path fill="#00cbe0" d="M.5 19.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1ZM18.5 1.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1Z" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M5.206 18.794 18.794 5.206M.5 19.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1ZM18.5 1.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1Z" />
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Linked Data'),
          ],
        ),
      );
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_ROLE)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param': TOKEN_TYPE_ROLE,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path fill="#00cbe0" d="m1.5 15.5.739 4.8A2 2 0 0 0 4.216 22h15.568a2 2 0 0 0 1.977-1.7l.739-4.8a1 1 0 0 0 1-1V8a2 2 0 0 0-2-2h-19a2 2 0 0 0-2 2v6.5a1 1 0 0 0 1 1" />
<path fill="#fff" d="M21.5 6h-19a2 2 0 0 0-2 2v3.5a2 2 0 0 1 2-2h19a2 2 0 0 1 2 2V8a2 2 0 0 0-2-2" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="m1.5 15.5.739 4.8A2 2 0 0 0 4.216 22h15.568a2 2 0 0 0 1.977-1.7l.739-4.8a1 1 0 0 0 1-1V8a2 2 0 0 0-2-2h-19a2 2 0 0 0-2 2v6.5a1 1 0 0 0 1 1M16.5 6h-9V5a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3ZM1.5 15.5H10M14 15.5h8.5" />
<path fill="#fff" d="M10 15.5a2 2.5 0 1 0 4 0 2 2.5 0 1 0-4 0" />
<path fill="#00cbe0" d="M12 15.929a2.03 2.03 0 0 1-1.816-1.464A3 3 0 0 0 10 15.5a2.29 2.29 0 0 0 2 2.5 2.29 2.29 0 0 0 2-2.5 3 3 0 0 0-.184-1.035A2.03 2.03 0 0 1 12 15.929" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M10 15.5a2 2.5 0 1 0 4 0 2 2.5 0 1 0-4 0" />
`,
            ),
            createElement(
              'span',
              { class: 'font-medium' },
              'Role or Asset Type',
            ),
          ],
        ),
      );
    } else if (
      this.enabledTokenTypes.includes(TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE)
    ) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param':
              TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path fill="#00cbe0" d="m1.5 15.5.739 4.8A2 2 0 0 0 4.216 22h15.568a2 2 0 0 0 1.977-1.7l.739-4.8a1 1 0 0 0 1-1V8a2 2 0 0 0-2-2h-19a2 2 0 0 0-2 2v6.5a1 1 0 0 0 1 1" />
<path fill="#fff" d="M21.5 6h-19a2 2 0 0 0-2 2v3.5a2 2 0 0 1 2-2h19a2 2 0 0 1 2 2V8a2 2 0 0 0-2-2" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="m1.5 15.5.739 4.8A2 2 0 0 0 4.216 22h15.568a2 2 0 0 0 1.977-1.7l.739-4.8a1 1 0 0 0 1-1V8a2 2 0 0 0-2-2h-19a2 2 0 0 0-2 2v6.5a1 1 0 0 0 1 1M16.5 6h-9V5a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3ZM1.5 15.5H10M14 15.5h8.5" />
<path fill="#fff" d="M10 15.5a2 2.5 0 1 0 4 0 2 2.5 0 1 0-4 0" />
<path fill="#00cbe0" d="M12 15.929a2.03 2.03 0 0 1-1.816-1.464A3 3 0 0 0 10 15.5a2.29 2.29 0 0 0 2 2.5 2.29 2.29 0 0 0 2-2.5 3 3 0 0 0-.184-1.035A2.03 2.03 0 0 1 12 15.929" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M10 15.5a2 2.5 0 1 0 4 0 2 2.5 0 1 0-4 0" />
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Employee role'),
          ],
        ),
      );
    } else if (this.enabledTokenTypes.includes(TOKEN_TYPE_ALIAS_ASSET_TYPE)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param':
              TOKEN_TYPE_ALIAS_ASSET_TYPE,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path fill="#00cbe0" d="m1.5 15.5.739 4.8A2 2 0 0 0 4.216 22h15.568a2 2 0 0 0 1.977-1.7l.739-4.8a1 1 0 0 0 1-1V8a2 2 0 0 0-2-2h-19a2 2 0 0 0-2 2v6.5a1 1 0 0 0 1 1" />
<path fill="#fff" d="M21.5 6h-19a2 2 0 0 0-2 2v3.5a2 2 0 0 1 2-2h19a2 2 0 0 1 2 2V8a2 2 0 0 0-2-2" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="m1.5 15.5.739 4.8A2 2 0 0 0 4.216 22h15.568a2 2 0 0 0 1.977-1.7l.739-4.8a1 1 0 0 0 1-1V8a2 2 0 0 0-2-2h-19a2 2 0 0 0-2 2v6.5a1 1 0 0 0 1 1M16.5 6h-9V5a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3ZM1.5 15.5H10M14 15.5h8.5" />
<path fill="#fff" d="M10 15.5a2 2.5 0 1 0 4 0 2 2.5 0 1 0-4 0" />
<path fill="#00cbe0" d="M12 15.929a2.03 2.03 0 0 1-1.816-1.464A3 3 0 0 0 10 15.5a2.29 2.29 0 0 0 2 2.5 2.29 2.29 0 0 0 2-2.5 3 3 0 0 0-.184-1.035A2.03 2.03 0 0 1 12 15.929" />
<path fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" d="M10 15.5a2 2.5 0 1 0 4 0 2 2.5 0 1 0-4 0" />
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Asset type'),
          ],
        ),
      );
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_SITE)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param': TOKEN_TYPE_SITE,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path d="M0.5 21.5a11.5 2 0 1 0 23 0 11.5 2 0 1 0 -23 0" fill="#00cbe0" stroke-width="1"></path>
<path d="M12 0.5a6.856 6.856 0 0 1 6.855 6.856c0 3.215 -4.942 11.185 -6.434 13.517a0.5 0.5 0 0 1 -0.842 0c-1.492 -2.332 -6.434 -10.3 -6.434 -13.517A6.855 6.855 0 0 1 12 0.5Z" fill="#00cbe0" stroke-width="1"></path>
<path d="M12 21.1a0.5 0.5 0 0 1 -0.421 -0.231c-1.492 -2.332 -6.434 -10.3 -6.434 -13.517A6.855 6.855 0 0 1 12 0.5Z" fill="#ffffff" stroke-width="1"></path>
<path d="M8.5 7a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0 -7 0" fill="#ffffff" stroke-width="1"></path>
<path d="M12 10.5a3.5 3.5 0 0 1 0 -7Z" fill="#adbcb9" stroke-width="1"></path>
<path d="M8.5 7a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0 -7 0" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M12 0.5a6.856 6.856 0 0 1 6.855 6.856c0 3.215 -4.942 11.185 -6.434 13.517a0.5 0.5 0 0 1 -0.842 0c-1.492 -2.332 -6.434 -10.3 -6.434 -13.517A6.855 6.855 0 0 1 12 0.5Z" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M17 19.7c3.848 0.324 6.5 1.009 6.5 1.8 0 1.105 -5.148 2 -11.5 2S0.5 22.605 0.5 21.5c0 -0.79 2.635 -1.473 6.458 -1.8" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Site'),
          ],
        ),
      );
    } else if (this.enabledTokenTypes.includes(TOKEN_TYPE_ALIAS_LOCATED_AT)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param':
              TOKEN_TYPE_ALIAS_LOCATED_AT,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path d="M0.5 21.5a11.5 2 0 1 0 23 0 11.5 2 0 1 0 -23 0" fill="#00cbe0" stroke-width="1"></path>
<path d="M12 0.5a6.856 6.856 0 0 1 6.855 6.856c0 3.215 -4.942 11.185 -6.434 13.517a0.5 0.5 0 0 1 -0.842 0c-1.492 -2.332 -6.434 -10.3 -6.434 -13.517A6.855 6.855 0 0 1 12 0.5Z" fill="#00cbe0" stroke-width="1"></path>
<path d="M12 21.1a0.5 0.5 0 0 1 -0.421 -0.231c-1.492 -2.332 -6.434 -10.3 -6.434 -13.517A6.855 6.855 0 0 1 12 0.5Z" fill="#ffffff" stroke-width="1"></path>
<path d="M8.5 7a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0 -7 0" fill="#ffffff" stroke-width="1"></path>
<path d="M12 10.5a3.5 3.5 0 0 1 0 -7Z" fill="#adbcb9" stroke-width="1"></path>
<path d="M8.5 7a3.5 3.5 0 1 0 7 0 3.5 3.5 0 1 0 -7 0" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M12 0.5a6.856 6.856 0 0 1 6.855 6.856c0 3.215 -4.942 11.185 -6.434 13.517a0.5 0.5 0 0 1 -0.842 0c-1.492 -2.332 -6.434 -10.3 -6.434 -13.517A6.855 6.855 0 0 1 12 0.5Z" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M17 19.7c3.848 0.324 6.5 1.009 6.5 1.8 0 1.105 -5.148 2 -11.5 2S0.5 22.605 0.5 21.5c0 -0.79 2.635 -1.473 6.458 -1.8" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Located at'),
          ],
        ),
      );
    }

    if (this.enabledTokenTypes.includes(TOKEN_TYPE_TITLE)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param': TOKEN_TYPE_TITLE,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path d="M21.207 4.5a1 1 0 0 1 0.293 0.707V22.5a1 1 0 0 1 -1 1h-17a1 1 0 0 1 -1 -1v-21a1 1 0 0 1 1 -1h13.293a1 1 0 0 1 0.707 0.291Z" fill="#ffffff" stroke-width="1"></path>
<path d="M19.321 2.613 17.5 0.791A1 1 0 0 0 16.793 0.5H3.5a1 1 0 0 0 -1 1v17.933Z" fill="#adbcb9" stroke-width="1"></path>
<path d="M14.5 5.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 14.5h13" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 17.5h13" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 20.5h13" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M14.5 8.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M14.5 11.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 5.5v-1h6v1" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M8.5 4.5v7" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M6.5 11.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M21.207 4.5a1 1 0 0 1 0.293 0.707V22.5a1 1 0 0 1 -1 1h-17a1 1 0 0 1 -1 -1v-21a1 1 0 0 1 1 -1h13.293a1 1 0 0 1 0.707 0.291Z" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Title'),
          ],
        ),
      );
    } else if (this.enabledTokenTypes.includes(TOKEN_TYPE_ALIAS_NAME)) {
      menuGroup.append(
        createElement(
          'button',
          {
            type: 'button',
            class: 'disclosure-menu__item rounded-xs',
            'data-action': 'search-bar--token-type-modal#pick',
            'data-search-bar--token-type-modal-type-param':
              TOKEN_TYPE_ALIAS_NAME,
          },
          [
            createSvg(
              { viewBox: '0 0 25 24', class: 'size-5 ml-1 mr-3' },
              `
<path d="M21.207 4.5a1 1 0 0 1 0.293 0.707V22.5a1 1 0 0 1 -1 1h-17a1 1 0 0 1 -1 -1v-21a1 1 0 0 1 1 -1h13.293a1 1 0 0 1 0.707 0.291Z" fill="#ffffff" stroke-width="1"></path>
<path d="M19.321 2.613 17.5 0.791A1 1 0 0 0 16.793 0.5H3.5a1 1 0 0 0 -1 1v17.933Z" fill="#adbcb9" stroke-width="1"></path>
<path d="M14.5 5.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 14.5h13" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 17.5h13" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 20.5h13" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M14.5 8.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M14.5 11.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M5.5 5.5v-1h6v1" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M8.5 4.5v7" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M6.5 11.5h4" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
<path d="M21.207 4.5a1 1 0 0 1 0.293 0.707V22.5a1 1 0 0 1 -1 1h-17a1 1 0 0 1 -1 -1v-21a1 1 0 0 1 1 -1h13.293a1 1 0 0 1 0.707 0.291Z" fill="none" stroke="#002e72" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>
`,
            ),
            createElement('span', { class: 'font-medium' }, 'Name'),
          ],
        ),
      );
    }

    disclosureWrapper.append(menuGroup);
    this.tokenTypeModal.append(disclosureWrapper);
    this.inputContainerTarget.append(this.tokenTypeModal);
  }

  private deflateTokenTypeMenu() {
    const ariaControls = (
      this.inputTarget.getAttribute('aria-controls') || ''
    ).split(' ');
    if (!ariaControls.includes('token-type-options')) {
      return;
    }

    ariaControls.splice(ariaControls.indexOf('token-type-options'), 1);
    this.inputTarget.setAttribute('aria-controls', ariaControls.join(' '));
    this.tokenTypeModal?.remove();

    this.tokenTypeModal = undefined;
  }

  private inflateTokenOperatorMenu() {
    this.inputContainerTarget.classList.add('relative');

    setAttributes(this.inputTarget, {
      'aria-expanded': false,
      'aria-controls': [
        this.inputTarget.getAttribute('aria-controls') || '',
        'token-operator-options',
      ]
        .join(' ')
        .trim(),
    });

    this.tokenOperatorModal = createElement('div', {
      id: 'token-operator-options',
      'data-controller': 'search-bar--token-operator-modal',
      hidden: '',
    });

    this.tokenOperatorModal.classList.add(
      'absolute',
      // 'transition',
      // 'ease-in',
      // 'duration-75',
      'transform',
      // 'opacity-1',
      // 'scale-95',
      'top-[36px]',
      'left-[12px]',
      'z-10',
    );

    const disclosureWrapper = createElement(
      'div',
      {
        'data-component': 'generic/disclosure-menu',
        role: 'listbox',
        class: 'p-1',
        style:
          'min-width: 200px; max-width: 380px; max-width: clamp(200px, 80vw, 240px);',
      },
      /*createElement(
        'div',
        {
          class: 'disclosure-menu__header sr-only',
          role: 'none',
        },
        [
          createElement(
            'p',
            { role: 'none', class: 'sr-only },
            createElement('strong', { class: 'sr-only' }, 'Token operator options'),
          ),
        ],
      ),*/
    );

    const menuGroup = createElement('div', {
      role: 'none',
      class: 'disclosure-menu__item-group',
    });

    menuGroup.append(
      createElement(
        'button',
        {
          type: 'button',
          class: 'disclosure-menu__item rounded-xs',
          'data-action': 'search-bar--token-operator-modal#pick',
          'data-operator': TOKEN_OPERATOR_EQUALS,
          'data-search-bar--token-operator-modal-operator-param':
            TOKEN_OPERATOR_EQUALS,
        },
        [
          createElement(
            'span',
            { class: 'inline-block size-5 ml-1 mr-3 font-mono' },
            TOKEN_OPERATOR_EQUALS,
          ),
          createElement(
            'span',
            { class: 'opacity-75 text-right ml-auto', 'data-target': 'label' },
            'is',
          ),
        ],
      ),
    );

    menuGroup.append(
      createElement(
        'button',
        {
          type: 'button',
          class: 'disclosure-menu__item rounded-xs',
          'data-action': 'search-bar--token-operator-modal#pick',
          'data-operator': TOKEN_OPERATOR_ANY_OF,
          'data-search-bar--token-operator-modal-operator-param':
            TOKEN_OPERATOR_ANY_OF,
        },
        [
          createElement(
            'span',
            { class: 'inline-block size-5 ml-1 mr-3 font-mono' },
            '·',
          ),
          createElement(
            'span',
            { class: 'opacity-75 text-right ml-auto', 'data-target': 'label' },
            'is one of',
          ),
        ],
      ),
    );

    menuGroup.append(
      createElement(
        'button',
        {
          type: 'button',
          class: 'disclosure-menu__item rounded-xs',
          'data-action': 'search-bar--token-operator-modal#pick',
          'data-operator': TOKEN_OPERATOR_NEQUALS,
          'data-search-bar--token-operator-modal-operator-param':
            TOKEN_OPERATOR_NEQUALS,
        },
        [
          createElement(
            'span',
            { class: 'inline-block size-5 ml-1 mr-3 font-mono' },
            TOKEN_OPERATOR_NEQUALS,
          ),
          createElement(
            'span',
            { class: 'opacity-75 text-right ml-auto', 'data-target': 'label' },
            'is not',
          ),
        ],
      ),
    );

    disclosureWrapper.append(menuGroup);
    this.tokenOperatorModal.append(disclosureWrapper);
    this.inputContainerTarget.append(this.tokenOperatorModal);
  }

  private deflateTokenOperatorMenu() {
    const ariaControls = (
      this.inputTarget.getAttribute('aria-controls') || ''
    ).split(' ');
    if (!ariaControls.includes('token-operator-options')) {
      return;
    }

    ariaControls.splice(ariaControls.indexOf('token-operator-options'), 1);
    this.inputTarget.setAttribute('aria-controls', ariaControls.join(' '));
    this.tokenOperatorModal?.remove();

    this.tokenOperatorModal = undefined;
  }

  private inflateTokenValueMenu() {
    this.inputContainerTarget.classList.add('relative');

    setAttributes(this.inputTarget, {
      'aria-expanded': false,
      'aria-controls': [
        this.inputTarget.getAttribute('aria-controls') || '',
        'token-value-options',
      ]
        .join(' ')
        .trim(),
    });

    this.tokenValueModal = createElement('div', {
      id: 'token-operator-options',
      'data-controller': 'search-bar--token-value-modal',
      'data-search-bar--token-value-modal-autocomplete-urls-value':
        this.autocompleteUrlsValue,
      'data-search-bar--token-value-modal-max-autocomplete-options-value':
        this.maxAutocompleteOptionsValue,
      hidden: '',
    });

    this.tokenValueModal.classList.add(
      'absolute',
      // 'transition',
      // 'ease-in',
      // 'duration-75',
      'transform',
      // 'opacity-1',
      // 'scale-95',
      'top-[36px]',
      'left-[12px]',
      'z-10',
    );

    const disclosureWrapper = createElement(
      'div',
      {
        'data-component': 'generic/disclosure-menu',
        role: 'listbox',
        class: 'p-1',
        style:
          'min-width: 200px; max-width: 380px; max-width: clamp(200px, 80vw, 240px);',
      },
      /*createElement(
        'div',
        {
          class: 'disclosure-menu__header sr-only',
          role: 'none',
        },
        [
          createElement(
            'p',
            { role: 'none', class: 'sr-only },
            createElement('strong', { class: 'sr-only' }, 'Token operator options'),
          ),
        ],
      ),*/
    );

    const menuGroup = createElement('div', {
      role: 'none',
      class: 'disclosure-menu__item-group',
    });

    menuGroup.append(
      createElement(
        'button',
        {
          type: 'button',
          disabled: '',
          class: 'disclosure-menu__item rounded-xs',
        },
        [createElement('span', {}, 'No matches found')],
      ),
    );

    disclosureWrapper.append(menuGroup);
    this.tokenValueModal.append(disclosureWrapper);
    this.inputContainerTarget.append(this.tokenValueModal);
  }

  private deflateTokenValueMenu() {
    const ariaControls = (
      this.inputTarget.getAttribute('aria-controls') || ''
    ).split(' ');
    if (!ariaControls.includes('token-value-options')) {
      return;
    }

    ariaControls.splice(ariaControls.indexOf('token-value-options'), 1);
    this.inputTarget.setAttribute('aria-controls', ariaControls.join(' '));
    this.tokenValueModal?.remove();

    this.tokenValueModal = undefined;
  }

  private inflateClearQuery() {
    const icon = createSvg(
      { viewBox: '0 0 16 16', fill: 'currentColor', class: 'size-4' },
      `<path fill-rule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm2.78-4.22a.75.75 0 0 1-1.06 0L8 9.06l-1.72 1.72a.75.75 0 1 1-1.06-1.06L6.94 8 5.22 6.28a.75.75 0 0 1 1.06-1.06L8 6.94l1.72-1.72a.75.75 0 1 1 1.06 1.06L9.06 8l1.72 1.72a.75.75 0 0 1 0 1.06Z" clip-rule="evenodd" />
<title>Clear query</title>`,
    );
    this.clearButton = createElement(
      'button',
      {
        type: 'button',
        'data-action': 'search-bar#onClearQuery',
        'data-target': 'clear',
        class:
          '--neutral border-r-0 button rounded-none px-0 w-[44px] shrink-0 --icon',
      },
      icon,
    );
    this.inputContainerTarget.append(this.clearButton);
  }

  private deflateClearQuery() {
    const button = this.inputContainerTarget.querySelector(
      'button[data-target="clear"]',
    );
    if (!button) {
      return;
    }

    button.remove();
  }

  private buttonizeIcon() {
    const icon = this.iconTarget;
    const button = createElement(
      'button',
      {
        type: 'submit',
        class: 'button --neutral --icon --xs w-[44px]  shrink-0',
      },
      icon,
    );
    icon.classList.add('size-5');
    this.inputContainerTarget.append(button);
  }

  private deButtonizeIcon() {
    const icon = this.iconTarget;
    const button = this.iconTarget.parentElement;
    if (button) {
      button.remove();
      icon.classList.remove('size-5');
    }

    this.inputTarget.before(icon);
  }

  private inflateHiddenInput() {
    this.hiddenInput = createElement('input', {
      type: 'hidden',
      value: '',
      name: 'q',
    });

    this.inputTarget.removeAttribute('value');
    this.inputTarget.setAttribute('name', '_q');
    this.inputTarget.classList.add('rounded-none!');
    this.inputGroupTarget.append(this.hiddenInput);

    this.calculateHiddenInputValue(false);
    this.calculateClearDisabledState();
  }

  private deflateHiddenInput() {
    this.inputTarget.setAttribute('value', this.hiddenInput?.value || '');
    this.hiddenInput?.remove();

    this.inputTarget.setAttribute('name', 'q');
  }

  private calculateHiddenInputValue(debounce = false) {
    if (!this.hiddenInput) {
      return;
    }

    clearTimeout(this.debounceTimerForHiddenInput);

    if (debounce) {
      this.debounceTimerForHiddenInput = setTimeout(
        () => this.calculateHiddenInputValue(false),
        300,
      );
      return;
    }

    const nextValue = [];
    this.tokens.forEach((token) => {
      nextValue.push(token.htmlValue);
    });

    if (this.inputTarget.value) {
      nextValue.push(this.inputTarget.value);
    }

    const nextHiddenInputValue = nextValue.filter(Boolean).join(' ');
    if (this.hiddenInput.value !== nextHiddenInputValue) {
      this.hiddenInput.value = nextHiddenInputValue;
    }
  }

  private calculateClearDisabledState() {
    const enabled = Boolean(this.hiddenInput?.value.trim().length);
    toggleAttribute('disabled', this.clearButton, !enabled);
  }

  private onInput() {
    this.calculateHiddenInputValue(true);
    this.calculateClearDisabledState();
    this.nextToken.value = this.inputTarget.value;

    emitSetQuery(this.inputTarget.value);
  }

  private onFocusInput(_event: FocusEvent) {
    this.gotoState(STATE_TOKEN_TYPE);
    toggleExpanded(this.inputTarget, true);
  }

  private onBlurInput(_event: FocusEvent) {
    // Just-in-time saving a current token
    if (this.nextToken.value.trim()) {
      if (
        this.state === STATE_TOKEN_VALUE ||
        this.state === STATE_TOKEN_VALUE_ACTIVE
      ) {
        //emitInput('Enter');
        //return;
      } else {
        const subTokens = this.tokenize(this.nextToken.value);
        this.nextToken = subTokens.shift()!;
        while (subTokens.length > 0) {
          this.commitToken();
          this.nextToken = subTokens.shift()!;
        }
      }

      this.inputTarget.value = '';
      this.commitToken();
    }

    this.gotoState(STATE_BLURRED);
    toggleExpanded(this.inputTarget, false);

    this.calculateHiddenInputValue(false);
    this.calculateClearDisabledState();
  }

  private onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown': {
        switch (this.state) {
          case STATE_TOKEN_OPERATOR: {
            emitInput(event.key);
            this.gotoState(STATE_TOKEN_OPERATOR_ACTIVE);
            break;
          }

          case STATE_TOKEN_TYPE: {
            emitInput(event.key);
            this.gotoState(STATE_TOKEN_TYPE_ACTIVE);
            break;
          }

          case STATE_TOKEN_VALUE: {
            emitInput(event.key);
            this.gotoState(STATE_TOKEN_VALUE_ACTIVE);
            break;
          }

          case STATE_TOKEN_OPERATOR_ACTIVE:
          case STATE_TOKEN_TYPE_ACTIVE:
          case STATE_TOKEN_VALUE_ACTIVE: {
            emitInput(event.key);
            break;
          }
        }

        event.preventDefault();
        event.stopPropagation();
        break;
      }

      case 'ArrowUp': {
        switch (this.state) {
          case STATE_TOKEN_OPERATOR: {
            emitInput(event.key);
            this.gotoState(STATE_TOKEN_OPERATOR_ACTIVE);
            break;
          }

          case STATE_TOKEN_TYPE: {
            emitInput(event.key);
            this.gotoState(STATE_TOKEN_TYPE_ACTIVE);
            break;
          }

          case STATE_TOKEN_VALUE: {
            emitInput(event.key);
            this.gotoState(STATE_TOKEN_VALUE_ACTIVE);
            break;
          }

          case STATE_TOKEN_OPERATOR_ACTIVE:
          case STATE_TOKEN_TYPE_ACTIVE:
          case STATE_TOKEN_VALUE_ACTIVE: {
            emitInput(event.key);
            break;
          }
        }

        event.preventDefault();
        event.stopPropagation();
        break;
      }

      case 'Escape': {
        switch (this.state) {
          case STATE_TOKEN_OPERATOR_ACTIVE: {
            emit(EVENT_ESCAPE);
            this.gotoState(STATE_TOKEN_OPERATOR);
            break;
          }

          case STATE_TOKEN_TYPE_ACTIVE: {
            emit(EVENT_ESCAPE);
            this.gotoState(STATE_TOKEN_TYPE);

            break;
          }

          case STATE_TOKEN_VALUE_ACTIVE: {
            emit(EVENT_ESCAPE);
            this.gotoState(STATE_TOKEN_VALUE);
            break;
          }

          case STATE_TOKEN_OPERATOR:
          case STATE_TOKEN_TYPE:
          case STATE_TOKEN_VALUE: {
            this.resetToken();
            this.gotoState(STATE_TOKEN_TYPE);
            break;
          }
        }

        event.preventDefault();
        event.stopPropagation();
        break;
      }

      case 'Enter': {
        switch (this.state) {
          case STATE_TOKEN_OPERATOR_ACTIVE:
          case STATE_TOKEN_TYPE_ACTIVE:
          case STATE_TOKEN_VALUE:
          case STATE_TOKEN_VALUE_ACTIVE: {
            emitInput(event.key);
            event.preventDefault();
            event.stopPropagation();
            break;
          }

          default: {
            this.calculateHiddenInputValue(false);
            this.iconTarget
              .closest<HTMLButtonElement>('button[type="submit"]')
              ?.focus();
            this.inputTarget.form?.requestSubmit();
            break;
          }
        }

        break;
      }

      case 'Home': {
        switch (this.state) {
          case STATE_TOKEN_OPERATOR_ACTIVE:
          case STATE_TOKEN_TYPE_ACTIVE:
          case STATE_TOKEN_VALUE_ACTIVE: {
            emitInput(event.key);
            event.preventDefault();
            event.stopPropagation();
            break;
          }
        }

        break;
      }

      case 'End': {
        switch (this.state) {
          case STATE_TOKEN_OPERATOR_ACTIVE:
          case STATE_TOKEN_TYPE_ACTIVE:
          case STATE_TOKEN_VALUE_ACTIVE: {
            emitInput(event.key);
            event.preventDefault();
            event.stopPropagation();
            break;
          }
        }

        break;
      }

      /*
      default: {
        switch (this.state) {
          case STATE_BLURRED: {
            // do nothing
            break;
          }
          default: {
            setTimeout(() => {
              emitSetQuery(this.inputTarget.value);
            }, 1);
          }
        }
      }
        */
    }
  }

  private onPickedTokenType(event: unknown) {
    if (!event || typeof event !== 'object' || !('type' in event)) {
      return;
    }

    if (typeof event.type !== 'string' || !isValidTokenType(event.type)) {
      return;
    }

    console.debug(`[search-bar] picked type: ${event.type}`);
    this.setNextTokenType(event.type);
    this.gotoState(STATE_TOKEN_OPERATOR_ACTIVE);
    this.inputTarget.focus();
  }

  private onPickedTokenOperator(event: unknown) {
    if (!event || typeof event !== 'object' || !('operator' in event)) {
      return;
    }

    if (
      typeof event.operator !== 'string' ||
      !isValidOperator(event.operator)
    ) {
      return;
    }

    console.debug(`[search-bar] picked operator: ${event.operator}`);
    this.setNextTokenOperator(event.operator);
    this.gotoState(STATE_TOKEN_VALUE_ACTIVE);
    this.inputTarget.focus();
  }

  private onPickedTokenValue(event: unknown) {
    if (!isEventPickValue(event)) {
      return;
    }

    console.debug(`[search-bar] picked value: ${event.value}`);
    this.inputTarget.value = '';

    this.setNextTokenValue(event.value, event.label);
    this.commitToken();

    this.gotoState(STATE_TOKEN_TYPE);
    this.inputTarget.focus();
  }

  private onChangedModalLayout(_event: unknown) {
    switch (this.state) {
      case STATE_TOKEN_TYPE:
      case STATE_TOKEN_TYPE_ACTIVE: {
        this.anchor(this.tokenTypeModal);
        break;
      }

      case STATE_TOKEN_OPERATOR:
      case STATE_TOKEN_OPERATOR_ACTIVE: {
        this.anchor(this.tokenOperatorModal);
        break;
      }

      case STATE_TOKEN_VALUE:
      case STATE_TOKEN_VALUE_ACTIVE: {
        this.anchor(this.tokenValueModal);
        break;
      }
    }
  }

  private gotoState(nextState: SearchBarState) {
    if (this.state === nextState) {
      return;
    }

    console.log(`[search-bar] goto ${nextState}`);

    this.state = nextState;

    switch (nextState) {
      case STATE_TOKEN_TYPE: {
        this.resetToken();
        this.anchor(this.tokenTypeModal);
        this.inputTarget.setAttribute('aria-haspopup', 'list');
        this.inputTarget.setAttribute('aria-autocomplete', 'none');
        this.inputTarget.removeAttribute('role');

        emitSetState(nextState);
        break;
      }

      case STATE_TOKEN_TYPE_ACTIVE: {
        this.anchor(this.tokenTypeModal);
        this.inputTarget.setAttribute('aria-haspopup', 'list');
        this.inputTarget.setAttribute('aria-autocomplete', 'none');
        this.inputTarget.removeAttribute('role');

        // Next frame so all current events are handled;
        emitSetState(this.state);
        break;
      }

      case STATE_TOKEN_OPERATOR:
      case STATE_TOKEN_OPERATOR_ACTIVE: {
        this.anchor(this.tokenOperatorModal);
        this.inputTarget.setAttribute('aria-haspopup', 'list');
        this.inputTarget.setAttribute('aria-autocomplete', 'none');
        this.inputTarget.removeAttribute('role');

        // Next frame so all current events are handled;
        emitSetState(nextState, {
          type: this.nextToken.type,
        });

        break;
      }

      case STATE_TOKEN_VALUE:
      case STATE_TOKEN_VALUE_ACTIVE: {
        this.anchor(this.tokenValueModal);
        this.inputTarget.setAttribute('aria-haspopup', 'list');
        this.inputTarget.setAttribute('aria-autocomplete', 'list');
        this.inputTarget.setAttribute('role', 'combobox');

        // Next frame so all current events are handled;
        emitSetState(nextState, {
          type: this.nextToken.type,
          operator: this.nextToken.operator,
          value: this.nextToken.value,
          // label: this.nextToken.valueHtmlString,
        });

        break;
      }

      case STATE_BLURRED: {
        this.resetToken();
        this.inputTarget.removeAttribute('aria-haspopup');
        this.inputTarget.removeAttribute('aria-autocomplete');
        this.inputTarget.removeAttribute('role');

        // Next frame so all current events are handled;
        emitSetState(nextState);
      }
    }
  }

  public onClearQuery() {
    this.tokens.splice(0, this.tokens.length);
    this.inputTarget.value = '';

    this.inputGroupTarget
      .querySelectorAll('[data-token-id]')
      .forEach((token) => token.remove());
    this.resetToken();

    this.calculateHiddenInputValue(false);
    this.calculateClearDisabledState();
  }

  private commitToken() {
    console.log(`[search-bar] commit: ${this.nextToken.htmlValue}`);

    if (this.nextToken.htmlValue) {
      this.tokens.push(this.nextToken);
      this.drawToken(this.nextToken);
      this.calculateHiddenInputValue(false);
      this.calculateClearDisabledState();
    }
  }

  private resetToken() {
    this.nextToken = new Token(
      TOKEN_TYPE_INVALID,
      TOKEN_OPERATOR_INVALID,
      this.inputTarget.value,
    );
    this.redrawNextToken();
  }

  private setNextTokenType(type: TokenType) {
    this.nextToken.type = type;
    this.redrawNextToken();
  }

  private setNextTokenOperator(operator: TokenOperator) {
    this.nextToken.operator = operator;
    this.redrawNextToken();
  }

  private setNextTokenValue(value: string, label?: string) {
    this.nextToken.value = value;
    this.nextToken.labelledValue = parseElement(label) || undefined;

    this.redrawNextToken();
  }

  private redrawNextToken() {
    const prevNextTokenElement = this.element.querySelector(
      '[data-target="next-token"]',
    );

    if (
      !this.nextToken.type ||
      this.nextToken.type === TOKEN_TYPE_INVALID ||
      this.state === STATE_BLURRED
    ) {
      prevNextTokenElement?.remove();
      return;
    }

    const nextTokenElement =
      prevNextTokenElement ||
      createElement(
        'div',
        {
          'data-target': 'next-token',
          class:
            'border-l border-t border-b border-gray-300 dark:border-gray-700 flex gap-px px-1 text-xs whitespace-nowrap',
        },
        createElement(
          'button',
          {
            type: 'button',
            class: 'button --xs --neutral my-[3px]',
            disabled: '',
            'data-action':
              'search-bar#editToken pointerdown->search-bar#editToken:prevent',
            'data-search-bar-id-param': 'next',
            'data-search-bar-state-param': STATE_TOKEN_TYPE,
          },
          this.nextToken.typeLabel,
        ),
      );

    if (prevNextTokenElement) {
      const prevNextTokenType = prevNextTokenElement.querySelector(
        `[data-search-bar-state-param="${STATE_TOKEN_TYPE}"]`,
      )!;
      prevNextTokenType.textContent = this.nextToken.typeLabel;
    }

    if (this.nextToken.operator != TOKEN_OPERATOR_INVALID) {
      const prevNextTokenOperator = nextTokenElement.querySelector(
        `[data-search-bar-state-param="${STATE_TOKEN_OPERATOR}"]`,
      );

      if (prevNextTokenOperator) {
        prevNextTokenOperator.textContent = this.nextToken.operatorLabel;
      } else {
        nextTokenElement.append(
          createElement(
            'button',
            {
              type: 'button',
              class: 'button --xs --neutral my-[3px]',
              disabled: '',
              'data-action':
                'search-bar#editToken pointerdown->search-bar#editToken:prevent',
              'data-search-bar-id-param': 'next',
              'data-search-bar-state-param': STATE_TOKEN_OPERATOR,
            },
            this.nextToken.operatorLabel,
          ),
        );
      }
    }

    this.inputGroupTarget.insertBefore(nextTokenElement, this.inputTarget);
  }

  private drawToken(token: Token) {
    this.inputGroupTarget.insertBefore(
      token.createElement({ resolveUrls: this.resolveUrlsValue }),
      this.inputTarget,
    );
  }

  public editToken(event: unknown) {
    if (!event || typeof event !== 'object' || !('params' in event)) {
      return;
    }

    const params = event.params;

    if (
      !params ||
      typeof params !== 'object' ||
      !('id' in params) ||
      !('state' in params)
    ) {
      return;
    }

    if (
      ![STATE_TOKEN_TYPE, STATE_TOKEN_OPERATOR, STATE_TOKEN_VALUE].includes(
        params.state as string,
      )
    ) {
      return;
    }

    if (
      !document.activeElement ||
      !this.element.contains(document.activeElement)
    ) {
      this.inputTarget.focus();

      setTimeout(() => {
        this.editToken(event);
      }, 1);
      return;
    }

    const removedToken = this.removeToken(event);
    if (!removedToken) {
      return;
    }

    if (this.inputTarget.value === '') {
      this.inputTarget.value = removedToken.value;
      this.inputTarget.select();
    } else {
      removedToken.labelledValue = undefined;
      removedToken.value = this.inputTarget.value;
    }

    // Rollback token to expected state
    switch (params.state) {
      case STATE_TOKEN_TYPE: {
        removedToken.type = TOKEN_TYPE_INVALID;
        removedToken.operator = TOKEN_OPERATOR_INVALID;
        break;
      }
      case STATE_TOKEN_OPERATOR: {
        removedToken.operator = TOKEN_OPERATOR_INVALID;
        break;
      }
    }

    this.nextToken = removedToken;
    this.gotoState(params.state as SearchBarState);
    this.redrawNextToken();
  }

  public removeToken(event: unknown) {
    if (!event || typeof event !== 'object' || !('params' in event)) {
      return;
    }

    const params = event.params;

    if (!params || typeof params !== 'object' || !('id' in params)) {
      return;
    }

    const tokenToRemove = this.tokens.find((token) => token.id === params.id);
    if (!tokenToRemove) {
      return;
    }

    this.tokens.splice(this.tokens.indexOf(tokenToRemove), 1);
    this.inputGroupTarget
      .querySelector(
        `[data-target="token"][data-token-id="${tokenToRemove.id}"]`,
      )
      ?.remove();

    this.calculateHiddenInputValue(false);
    this.calculateClearDisabledState();

    // Move expanded dialog
    this.onChangedModalLayout({});

    return tokenToRemove;
  }

  private anchor(element: HTMLElement | undefined) {
    if (!element) {
      return;
    }

    element.classList.add('hidden');
    element.getBoundingClientRect();

    setTimeout(() => {
      this.inputGroupTarget.scrollTo(
        this.inputGroupTarget.scrollWidth - 240 - 50,
        0,
      );

      const { left } = getAnchoredPosition(element, this.inputTarget, {
        allowOutOfBounds: false,
      });
      element.style.left = `${left}px`;
      element.classList.remove('hidden');
    }, 1);
  }
}
